import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'
import { AccountBody, RoleReducerType } from 'redux/reducers/Accounts'
import {
  EReduxState,
  getBooleanStates,
  TReduxBooleanState
} from 'redux/utils/State'
import { EAccountRole, TAccount, TSignedInAccount } from 'types/GlobalAccount'
import { arrayToObject, formatUsername } from 'utils/functions'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

type AccountsSelectorType = {
  accounts: TAccount[]
  accountBody: AccountBody
} & TReduxBooleanState

const transformSignedInHomepalUser = (
  account: TSignedInAccount | null
): TAccount | null => {
  if (!account) {
    return null
  }

  const today = new Date()

  return {
    activated_at: today,
    created_at: today,
    company_group_id: null,
    email: account.email,
    first_name: account.first_name,
    id: account.id,
    invite_accepted_at: today,
    last_name: account.last_name,
    role: account.role,
    has_access_to_economy_settings: true,
    allow_access_to_analyses:
      account.allow_access_to_analyses,
    organizational_unit_id: account.organizational_unit_id
  }
}

export const selectAccounts = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AccountStore.data,
    (state: ApplicationState) => state.AccountStore.state
  ],
  (accountsBody, state): AccountsSelectorType => {
    if (state !== EReduxState.SUCCESS) {
      return { accounts: [], accountBody: {}, ...getBooleanStates(state) }
    }

    const accounts = Object.values(accountsBody)

    return { accounts, accountBody: accountsBody, ...getBooleanStates(state) }
  }
)

export const selectInternalAccounts = createSelector(
  [
    selectAccounts,
    (state: ApplicationState, includeCompanyGroupUsers: boolean) =>
      includeCompanyGroupUsers
  ],
  (selectedAccounts, includeCompanyGroupUsers): AccountsSelectorType => {
    const internalAccounts = selectedAccounts.accounts.filter(
      (account) => !account.company_group_id || includeCompanyGroupUsers
    )

    return {
      ...selectedAccounts,
      accounts: internalAccounts,
      accountBody: arrayToObject(internalAccounts)
    }
  }
)

export const selectAccount = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AuthStore.account,
    (state: ApplicationState) => state.AccountStore.data,
    (state: ApplicationState, id: string | null) => id
  ],
  (signedInAccount, accounts, id): TAccount | null => {
    if (!id) {
      return null
    }

    if (
      id === signedInAccount?.id &&
      (signedInAccount?.is_homepal_user ||
        signedInAccount?.is_homepal_demo_master)
    ) {
      return transformSignedInHomepalUser(signedInAccount)
    }

    return accounts[id] || null
  }
)

// select all available roles for customer
// [roles, loading]
export const selectRoles = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AccountStore.roles,
    (state: ApplicationState) => state.AccountStore.rolesState,
    (state: ApplicationState) => state.AuthStore.customer,
    (state: ApplicationState) => state.AuthStore.account
  ],
  (
    roles,
    rolesState,
    customer,
    user
  ): { roles: RoleReducerType } & TReduxBooleanState => {
    if (rolesState !== EReduxState.SUCCESS || !customer || !user) {
      return { roles: {}, ...getBooleanStates(rolesState) }
    }
    const parsedRoles: RoleReducerType = {}

    Object.values(roles).forEach((role) => {
      if (role.name === EAccountRole.ACCESS_HUB) {
        if (customer.allow_access.access_hub) {
          parsedRoles[role.name] = role
        }

        return
      }

      if (
        !role.only_visible_to_homepal ||
        user.is_homepal_user ||
        user.role === role.name
      ) {
        parsedRoles[role.name] = role
      }
    })

    return { roles: parsedRoles, ...getBooleanStates(rolesState) }
  }
)

export const selectAdmins = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AccountStore.data,
    (state: ApplicationState) => state.AccountStore.state
  ],
  (accounts, state): AccountsSelectorType => {
    if (state !== EReduxState.SUCCESS) {
      return { accounts: [], accountBody: {}, ...getBooleanStates(state) }
    }

    const admins = Object.values(accounts)
      .filter((item) => item.role === EAccountRole.ADMIN)
      .map((user) => ({ ...user, name: formatUsername(user) }))
      .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))

    return {
      accounts: admins,
      accountBody: arrayToObject(admins),
      ...getBooleanStates(state)
    }
  }
)
